import { STUDIO_ENV } from '@webplayer/constants/attributes';

import type { AttributeOptions, BuildEnv, ManifestType } from './attributes-core';
import { MODE } from './attributes-core';
import { booleanOrTrue, findOr, numberOr } from './helpers';

export function getManifest(params: URLSearchParams) {
  const manifestParam = params.get('manifest');
  if (manifestParam) {
    return manifestParam;
  }
  if (['encoded_id', 'id'].some((v) => params.get(v))) {
    return '';
  }
  const httpPrefix = import.meta.env.dev ? 'http' : 'https';
  return `${httpPrefix}://storage.googleapis.com/shaka-demo-assets/bbb-dark-truths-hls/hls.m3u8`;
}

type GetInitBuildParams = { params: URLSearchParams; defaultBuild: BuildEnv };
export function getInitBuild({ params, defaultBuild }: GetInitBuildParams) {
  const buildValue = params.get('build');
  const validValues: BuildEnv[] = ['prod', 'staging'];
  if (import.meta.env.DEV || import.meta.env.VITE_STAGING) {
    validValues.push('dev');
  }
  const buildMatch = validValues.find((v) => v === buildValue);
  if (!buildMatch) {
    return defaultBuild;
  }
  return buildMatch;
}

export function initAttributeOptions(params: URLSearchParams): AttributeOptions {
  return {
    manifest: getManifest(params),
    encodedManifest: params.get('encoded_manifest') || '',
    id: params.get('id') || null,
    type: findOr({
      arr: ['', 'event', 'library', 'playlist'],
      value: params.get('type'),
      fallback: '',
    }),
    env: findOr({
      arr: ['int', 'prod'],
      value: params.get('env'),
      fallback: 'int',
    }),
    manifestType: findOr<ManifestType>({
      arr: ['hls', 'dash'],
      value: params.get('manifestType'),
      fallback: 'dash',
    }),
    debug: params.get('debug') === 'true',
    startPos: numberOr({
      params,
      name: 'startPos',
      fallback: null,
    }),
    autoplay: booleanOrTrue({
      name: 'autoplay',
      params,
    }),
    bitrateLimitEnabled: booleanOrTrue({
      name: 'bitrateLimit',
      params,
    }),
    studio: findOr({
      arr: ['studio-prod', 'studio-int', 'studio-local', 'not-studio'],
      value: params.get('studio'),
      fallback: STUDIO_ENV.NOT_STUDIO,
    }),
    iframeId: params.get('iframeId') || null,
    playerProfileId: params.get('playerProfileId') || null,
    loop: params.get('loop') === 'true',
    chunkedTransfer: params.get('chunkedTransfer') === 'true',
    controls: booleanOrTrue({
      name: 'controls',
      params,
    }),
    background: params.get('background') === 'true',
    isIos: params.get('isIos') === 'true',
    scrubBar: booleanOrTrue({
      name: 'scrubBar',
      params,
    }),
    callToActionButton: booleanOrTrue({
      name: 'callToActionButton',
      params,
    }),
    mute: booleanOrTrue({
      name: 'mute',
      params,
    }),
    mode: MODE.DEFAULT,
  };
}
